import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Compact metadata table for slide-outs, drawers, and modals.
 * Figma Governance — Detail table (node 320-13846).
 *
 * Two-column rows: fixed-width label on the left, value on the right.
 * `layout="stacked"` puts the value under its label instead (narrow cards); the label can then carry an icon.
 * Wrapped in a subtle bordered container by default.
 *
 * @param {Array<{
 *   label: string|import('react').ReactNode,
 *   value: string|number|import('react').ReactNode,
 *   key?: string|number,
 *   labelClassName?: string,
 *   valueClassName?: string,
 *   className?: string,
 * }>} props.items
 * @param {boolean} [props.bordered=true]  Outer container border and padding.
 * @param {string} [props.labelWidth='140px']  Fixed width for the label column (inline layout).
 * @param {'inline'|'stacked'} [props.layout='inline']
 * @param {boolean} [props.divided=true]  Line between rows.
 * @param {string} [props.className]
 */
const MetaTable = ({
  items = [],
  bordered = true,
  labelWidth = '140px',
  layout = 'inline',
  divided = true,
  className = '',
}) => {
  const isStacked = layout === 'stacked';
  const list = Array.isArray(items) ? items : [];
  const lastIndex = list.length - 1;
  const labelStyle = useMemo(() => (isStacked ? undefined : { width: labelWidth }), [isStacked, labelWidth]);

  const rows = list.map((item, idx) => {
    const showDivider = divided && idx < lastIndex;
    const rowClass = [
      isStacked ? 'flex flex-col items-stretch gap-1 py-2.5' : 'flex items-center gap-[19px] py-2',
      item.className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div key={item.key ?? `meta-${idx}`} className="min-w-0">
        <div className={rowClass}>
          <div
            className={[
              isStacked ? 'text-xs font-medium leading-5 text-content' : 'shrink-0 text-xs font-medium leading-5 text-content',
              item.labelClassName,
            ]
              .filter(Boolean)
              .join(' ')}
            style={labelStyle}
          >
            {item.label}
          </div>
          <div
            className={[
              'min-w-0 flex-1 text-xs font-medium leading-5 text-content-secondary',
              item.valueClassName,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {item.value}
          </div>
        </div>
        {showDivider ? <div className="h-px w-full bg-edge" aria-hidden /> : null}
      </div>
    );
  });

  return (
    <div
      className={[
        bordered ? 'rounded-lg border border-edge p-2' : 'flex flex-col',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {rows}
    </div>
  );
};

MetaTable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node.isRequired,
      value: PropTypes.node.isRequired,
      labelClassName: PropTypes.string,
      valueClassName: PropTypes.string,
      className: PropTypes.string,
    }),
  ),
  bordered: PropTypes.bool,
  labelWidth: PropTypes.string,
  layout: PropTypes.oneOf(['inline', 'stacked']),
  divided: PropTypes.bool,
  className: PropTypes.string,
};

MetaTable.displayName = 'MetaTable';

export default memo(MetaTable);
